import React, { useState } from 'react';

import { useAuth } from "react-oidc-context";
import { DashboardData, DashboardDataGroup, DashboardDataItem } from "./types";
import { getClaimValue } from "../../utils/authUser";
import LimeClaimTypes from "../../models/limeClaimTypes";
import { composeUrl } from "../../utils/api";
import { formatViewDate } from "../../utils/dateFormat";
import OrganisationHealthScoreBlock from "./OrganisationHealthScoreBlock";
import FaceVoiceScanMonthlyChart from "./FaceVoiceScanMonthlyChart";
import ScanCompletenessPie from "./ScanCompletenessPie";
import ScanTypeCoveragePie from "./ScanTypeCoveragePie";
import {
  toScanCompletenessValue,
  toScanTypeCoverageValue,
} from "./scanCompleteness";
import { NO_SCAN_DATA_LAST_28_DAYS, FACE_VOICE_MONTHLY_LABEL, WEARABLE_DAYS_MONTHLY_LABEL, SIGNAL_ACTIVITY_HEADING, SIGNAL_ACTIVITY_PLACEHOLDER, allSignalsLines, lastDataUpdateLine, populationActivityLines, reportingColSpan, reportingPieTitle, reportingSectionNote, reportingTiles, ReportingTile, signalParticipationLines } from "./reportingScanDisplay";
import ProgressBar from "@ramonak/react-progress-bar";
import { portalPageSubtitle, portalPageTitle } from "../../theme/portalLayout";
import {
  dashboardAlert,
  dashboardGrid,
  dashboardHero,
  dashboardMemberProgressPercentAside,
  dashboardMemberProgressShell,
  dashboardMuted,
  dashboardPage,
  utcDataDisclaimer,
  dashboardStatCard,
  dashboardStatLabel,
  dashboardStatValue,
  dashboardWideCard,
} from "../../theme/dashboardLayout";

const memberProgressBarHeightPx = '22px';

/** Below this, @ramonak/react-progress-bar hides the in-fill label; floor fill width for readability. */
const memberProgressBarMinVisualPercent = 25;

function memberMetricBarVisualPercent(actualPercent: number): number {
  const p = Math.min(100, Math.max(0, Math.trunc(Number(actualPercent))));
  if (p <= 0) {
    return 0;
  }

  if (p < memberProgressBarMinVisualPercent) {
    return memberProgressBarMinVisualPercent;
  }

  return p;
}

/** Paid invoices use billing status id 2. */
const invoiceStatusPaidId = 2;

function LastInvoiceStatusCornerGlyph(props: {
  hasInvoice: boolean;
  statusId: number;
  statusName: string;
}): React.ReactElement | null {
  if (!props.hasInvoice) {
    return null;
  }

  const isPaid = props.statusId === invoiceStatusPaidId;
  const label = props.statusName ? `Invoice status: ${props.statusName}` : 'Invoice status';

  return (
    <div
      className="pointer-events-none absolute right-2 top-2 z-[1] sm:right-4 sm:top-4"
      role="img"
      aria-label={label}
    >
      {isPaid ? (
        <svg
          className="h-14 w-14 text-emerald-600 drop-shadow-[0_1px_2px_rgba(0,0,0,0.12)] sm:h-16 sm:w-16"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <path
            d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z"
            fill="currentColor"
            opacity="0.12"
          />
          <path
            d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="m8.5 12.5 2.2 2.2L15.5 10"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg
          className="h-14 w-14 text-error-red drop-shadow-[0_1px_2px_rgba(0,0,0,0.12)] sm:h-16 sm:w-16"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <path
            d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z"
            fill="currentColor"
            opacity="0.1"
          />
          <path
            d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="m9 9 6 6m0-6-6 6"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      )}
    </div>
  );
}

function MemberMetricProgressBar({ percent }: { percent: number }): React.ReactElement {
  const actual = Math.min(100, Math.max(0, Math.trunc(Number(percent))));
  const visual = memberMetricBarVisualPercent(actual);

  return (
    <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
      <div className={`min-w-0 flex-1 ${dashboardMemberProgressShell}`}>
        <ProgressBar
          completed={visual}
          maxCompleted={100}
          bgColor="#0d9488"
          baseBgColor="#e7edf8"
          height={memberProgressBarHeightPx}
          borderRadius="9999px"
          isLabelVisible={false}
          className="w-full"
        />
      </div>
      <span className={dashboardMemberProgressPercentAside}>{actual}%</span>
    </div>
  );
}

/**
 * Reporting rows (lg): OHS, then monthly chart when it has scans.
 * All-time reach and Current Population Activity keep the same headings and the same chart row.
 */
const REPORTING_GROUP_TITLE = 'Reporting';
const POPULATION_PULSE_HEADING = 'Population Pulse';
const POPULATION_PULSE_SUPPORTING_LINE =
  'A live view of how your population is engaging, changing and responding over time.';
const reportingGroupGrid =
  'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12 lg:gap-4';

function isScanCompletenessLabel(label: string): boolean {
  return label.startsWith('Scan completeness') || label.startsWith('Population Coverage');
}

function isScanTypeLabel(label: string): boolean {
  return (
    label.startsWith('Face scans') ||
    label.startsWith('Voice scans') ||
    label.startsWith('Wearables')
  );
}

function SignalMeasureHeading(props: { title: string; lines: string[] }): React.ReactElement {
  const slots = [0, 1, 2].map((index) => props.lines[index] ?? '');
  return (
    <span className="grid gap-0.5">
      <span>{props.title}</span>
      {slots.map((line, index) => (
        <span
          key={index}
          className={`min-h-[1.125rem] text-[13px] font-normal normal-case tracking-normal leading-tight ${
            index === 0 ? 'text-portal-teal/80' : 'text-portal-teal/70'
          } ${line ? '' : 'invisible'}`}
          aria-hidden={line ? undefined : true}
        >
          {line || '\u00a0'}
        </span>
      ))}
    </span>
  );
}

function reportingCardHeading(item: DashboardDataItem): React.ReactNode {
  const title = reportingPieTitle(item.label);
  if (isScanCompletenessLabel(item.label)) {
    const completeness = toScanCompletenessValue(item.value);
    if (!completeness) {
      return title;
    }

    const lines = allSignalsLines(completeness);
    return (
      <SignalMeasureHeading
        title={title}
        lines={[lines.reach, lines.scans, lines.wearableDays]}
      />
    );
  }

  if (!isScanTypeLabel(item.label)) {
    return title;
  }

  const coverage = toScanTypeCoverageValue(item.value);
  if (!coverage) {
    return title;
  }

  const lines = signalParticipationLines(item.label, coverage.uniqueUsers, coverage.totalEvents);
  return <SignalMeasureHeading title={title} lines={[lines.reach, lines.depth]} />;
}

export function DashboardGroupHeading(props: { title: string }): React.ReactElement {
  const isPopulationPulse = props.title === REPORTING_GROUP_TITLE;
  return (
    <div>
      <h2
        className={
          isPopulationPulse
            ? 'text-sm font-semibold tracking-wide text-portal-teal'
            : 'text-sm font-semibold uppercase tracking-wider text-portal-teal/55'
        }
      >
        {isPopulationPulse ? POPULATION_PULSE_HEADING : props.title}
      </h2>
      {isPopulationPulse ? (
        <p className="mt-1 max-w-3xl text-[13px] font-normal normal-case tracking-normal text-portal-teal/70">
          {POPULATION_PULSE_SUPPORTING_LINE}
        </p>
      ) : null}
    </div>
  );
}

function gridClassForGroup(groupTitle: string): string {
  if (groupTitle === REPORTING_GROUP_TITLE) {
    return reportingGroupGrid;
  }
  return dashboardGrid;
}

function itemIsWideChart(label: string, groupTitle: string): boolean {
  if (groupTitle === REPORTING_GROUP_TITLE) {
    return false;
  }
  return label === FACE_VOICE_MONTHLY_LABEL;
}

function readContextNumber(
  record: Record<string, unknown> | null,
  camel: string,
  pascal: string,
): number | null {
  if (record == null) {
    return null;
  }

  const raw = record[camel] ?? record[pascal];
  const value = Number(raw);
  return raw == null || !Number.isFinite(value) ? null : value;
}

function readContextString(
  record: Record<string, unknown> | null,
  camel: string,
  pascal: string,
): string | null {
  if (record == null) {
    return null;
  }

  const raw = record[camel] ?? record[pascal];
  return typeof raw === 'string' && raw.trim() !== '' ? raw : null;
}

export function renderItemBody(item: DashboardDataItem): React.ReactNode {
  if (item.label === 'Start Date') {
    return (
      <p className={dashboardStatValue}>
        {formatViewDate(new Date(String(item.value)))}
      </p>
    );
  }

  if (item.label === 'Organisation Health Score') {
    const payload = item.value as
      | string
      | number
      | {
          score?: string | number | null;
          Score?: string | number | null;
        }
      | null
      | undefined;
    let numericScore: number | null = null;
    if (payload != null && typeof payload === 'object' && !Array.isArray(payload)) {
      const raw = payload.score ?? payload.Score;
      numericScore = raw === null || raw === undefined ? null : Number(raw);
    } else if (payload !== null && payload !== undefined) {
      numericScore = Number(payload);
    }
    const contextRecord = payload != null && typeof payload === 'object' && !Array.isArray(payload)
      ? payload as Record<string, unknown>
      : null;
    return (
      <OrganisationHealthScoreBlock
        score={numericScore}
        updatedAt={readContextString(contextRecord, 'updatedAt', 'UpdatedAt')}
        contributorCount={readContextNumber(contextRecord, 'contributorCount', 'ContributorCount')}
        enrolledMembers={readContextNumber(contextRecord, 'enrolledMembers', 'EnrolledMembers')}
        previousScore={readContextNumber(contextRecord, 'previousScore', 'PreviousScore')}
        qualityCoefficient={readContextNumber(contextRecord, 'averageQualityCoefficient', 'AverageQualityCoefficient')}
      />
    );
  }

  if (item.label === FACE_VOICE_MONTHLY_LABEL || item.label === WEARABLE_DAYS_MONTHLY_LABEL) {
    const trends = Array.isArray(item.value) ? item.value : [];
    const wearableDays = item.label === WEARABLE_DAYS_MONTHLY_LABEL;
    return (
      <FaceVoiceScanMonthlyChart
        trends={trends}
        seriesName={wearableDays ? 'Wearable days' : 'Face and voice signals'}
        tooltipName={wearableDays ? 'Wearable days' : 'Face and voice signals'}
        valueNoun={wearableDays ? 'days' : 'signals'}
        emptyMessage={
          wearableDays
            ? 'No wearable day history yet for this organisation.'
            : 'No face or voice signal history yet for this organisation.'
        }
      />
    );
  }

  if (isScanCompletenessLabel(item.label)) {
    const completeness = toScanCompletenessValue(item.value);
    if (!completeness) {
      return <div className={dashboardAlert}>{item.errorMessage}</div>;
    }
    return <ScanCompletenessPie completeness={completeness} />;
  }

  if (isScanTypeLabel(item.label)) {
    const coverage = toScanTypeCoverageValue(item.value);
    if (!coverage) {
      return <div className={dashboardAlert}>{item.errorMessage}</div>;
    }
    return <ScanTypeCoveragePie label={item.label} coverage={coverage} />;
  }

  if (item.label === 'Eligible / Agreed Members') {
    const v = item.value as {
      eligibleMembers: number;
      agreedMembers: number;
      progressPercent: number;
    };
    return (
      <div className="mt-2 space-y-4">
        <p className={`${dashboardStatValue} tabular-nums`}>
          {v.eligibleMembers} / {v.agreedMembers}
        </p>
        <MemberMetricProgressBar percent={v.progressPercent} />
      </div>
    );
  }

  if (item.label === 'Enrolled') {
    const v = item.value as { membersCount: number; progressPercent: number };
    return (
      <div className="mt-2 space-y-4">
        <p className={`${dashboardStatValue} tabular-nums`}>{v.membersCount}</p>
        <MemberMetricProgressBar percent={v.progressPercent} />
      </div>
    );
  }

  if (item.label === 'Opted Out') {
    const v = item.value as { optedOutMembers: number; progressPercent: number };
    return (
      <div className="mt-2 space-y-4">
        <p className={`${dashboardStatValue} tabular-nums`}>{v.optedOutMembers}</p>
        <MemberMetricProgressBar percent={v.progressPercent} />
      </div>
    );
  }

  if (item.label === 'Last invoice') {
    const v = item.value as {
      hasInvoice: boolean;
      ref: string;
      date?: string;
      statusName: string;
      statusId: number;
      total?: number;
    };
    if (!v.hasInvoice) {
      return <p className={`${dashboardStatValue}`}>No invoices yet</p>;
    }
    const dateObj = v.date ? new Date(v.date) : undefined;
    const amount =
      v.total === undefined || v.total === null
        ? ''
        : v.total.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
    return (
      <div className="mt-2 space-y-3 pr-14 sm:pr-16">
        <p className={`${dashboardStatValue} break-words`}>{v.ref}</p>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={
              'inline-flex max-w-full items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase ' +
              'tracking-wide ring-1 ring-portal-teal/15 bg-portal-header-wash/90 text-portal-teal'
            }
          >
            {v.statusName}
          </span>
        </div>
        <dl className="grid gap-1 text-sm text-portal-teal/85">
          <div className="flex flex-wrap justify-between gap-x-4 gap-y-1">
            <dt className="text-portal-teal/60">Date</dt>
            <dd className="font-medium tabular-nums text-portal-teal">{formatViewDate(dateObj)}</dd>
          </div>
          {amount ? (
            <div className="flex flex-wrap justify-between gap-x-4 gap-y-1">
              <dt className="text-portal-teal/60">Total</dt>
              <dd className="font-medium tabular-nums text-portal-teal">{amount}</dd>
            </div>
          ) : null}
        </dl>
      </div>
    );
  }

  return <p className={dashboardStatValue}>{String(item.value)}</p>;
}

function UserDashboard() {
  const auth = useAuth();
  const [name, setName] = useState<string | undefined>();
  const [groups, setGroups] = useState<DashboardDataGroup[]>([]);

  React.useEffect(() => {
    setName(getClaimValue(auth.user, "nickname"));

    const orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
    const dashboardPromise = fetch(composeUrl(`organisations/${orgUuid}/dashboard`), {
      headers: [['Authorization', `Bearer ${auth.user?.access_token}`]],
      method: 'GET',
    }).then((response) => response.json() as Promise<DashboardData>);

    dashboardPromise.then((data) => {
      setGroups(data.groups);
    });
  }, [auth.user]);

  return (
    <div className={dashboardPage}>
      <header className={dashboardHero}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-portal-teal/55">
          Organisation overview
        </p>
        <h1 className={`mt-2 ${portalPageTitle}`}>Hello {name}</h1>
        <p className={`mt-3 max-w-2xl ${portalPageSubtitle}`}>
          Here&apos;s the pulse of your population today.
        </p>
      </header>

      {groups &&
        groups.map((group, gIndex) => (
          <section key={gIndex} className="space-y-4">
            {group.title ? <DashboardGroupHeading title={group.title} /> : null}
            <div className={gridClassForGroup(group.title)}>
              {(() => {
                const tiles: ReportingTile[] = group.title === REPORTING_GROUP_TITLE
                  ? reportingTiles(group.items)
                  : group.items.map((item) => ({ kind: 'item' as const, item }));
                const visibleLabels = tiles.flatMap((tile) =>
                  tile.kind === 'item' ? [tile.item.label] : [],
                );
                return tiles.map((tile, iIndex) => {
                if (tile.kind === 'heading') {
                  const activityLocked = tiles.some((candidate) => candidate.kind === 'activityPlaceholder');
                  const sectionNote = activityLocked && tile.text === SIGNAL_ACTIVITY_HEADING
                    ? null
                    : reportingSectionNote(tile.text);
                  const lastUpdate = lastDataUpdateLine(group.items);
                  const activityLines = !activityLocked && tile.text === SIGNAL_ACTIVITY_HEADING
                    ? populationActivityLines(
                      group.items.find((item) => item.label === 'Population Coverage (last 28 days)')?.value,
                    )
                    : [];
                  return (
                    <div
                      key={`${gIndex}-heading-${tile.text}`}
                      className="col-span-1 px-1 pt-2 sm:col-span-2 lg:col-span-12"
                    >
                      <h3 className="text-[14px] font-semibold leading-tight uppercase tracking-[0.14em] text-portal-teal/55">
                        {tile.text}
                      </h3>
                      {sectionNote ? (
                        <p className="mt-1 text-[13px] font-normal normal-case tracking-normal text-portal-teal/70">
                          {sectionNote}
                        </p>
                      ) : null}
                      {lastUpdate ? (
                        <p className="mt-1 text-[13px] font-normal normal-case tracking-normal text-portal-teal/70">
                          {lastUpdate}
                        </p>
                      ) : null}
                      {activityLines.length > 0 ? (
                        <ul className="mt-2 space-y-0.5 text-[13px] font-normal normal-case tracking-normal text-portal-teal/80">
                          {activityLines.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  );
                }

                if (tile.kind === 'activityPlaceholder') {
                  return (
                    <article
                      key={`${gIndex}-activity-placeholder`}
                      className={`${dashboardStatCard} lg:col-span-12`}
                    >
                      <p className={dashboardMuted}>{SIGNAL_ACTIVITY_PLACEHOLDER}</p>
                    </article>
                  );
                }

                if (tile.kind === 'last28Empty') {
                  return (
                    <article
                      key={`${gIndex}-last28-empty`}
                      className={`${dashboardStatCard} lg:col-span-12`}
                    >
                      <p className={dashboardMuted}>{NO_SCAN_DATA_LAST_28_DAYS}</p>
                    </article>
                  );
                }

                const item = tile.item;
                const wide = itemIsWideChart(item.label, group.title);
                const reportingSpan =
                  group.title === REPORTING_GROUP_TITLE
                    ? reportingColSpan(item.label, visibleLabels)
                    : '';
                return (
                  <article
                    key={`${gIndex}-${iIndex}`}
                    className={[
                      wide ? dashboardWideCard : dashboardStatCard,
                      reportingSpan,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {item.label === 'Last invoice' ? (
                      <LastInvoiceStatusCornerGlyph
                        hasInvoice={Boolean((item.value as { hasInvoice?: boolean }).hasInvoice)}
                        statusId={Number((item.value as { statusId?: number }).statusId)}
                        statusName={String((item.value as { statusName?: string }).statusName ?? '')}
                      />
                    ) : null}
                    <h3 className={dashboardStatLabel}>{reportingCardHeading(item)}</h3>
                    <div className="min-w-0 flex-1">{renderItemBody(item)}</div>
                    {item.details ? (
                      <p className={`mt-4 border-t border-portal-teal/10 pt-4 ${dashboardMuted}`}>
                        {item.details}
                      </p>
                    ) : null}
                  </article>
                );
              });
              })()}
            </div>
          </section>
        ))}
      <p className={`mt-8 ${dashboardMuted}`}>{utcDataDisclaimer}</p>
    </div>
  );
}

export default UserDashboard;
